import { useEffect, useRef, useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import { PlainTextHeader } from 'Deer/components/HeaderComponents/PlainTextHeader';
import { getApplicableL2MyTeamModules } from 'Deer/components/navigation/secondary/MyTeam/MyTeamModules';
import { staticRoutes } from 'Deer/constants/routes';
import { useViewFromQueryParam } from 'Deer/hooks/useViewFromQueryParam';
import { IntentDropdown } from 'Deer/intent/Reviews/components/IntentDropdown';
import { Layout } from 'Deer/intent/components/Layout/Layout';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setSelectedView } from 'reducers/viewsSlice';
import useDeerL1PageBasedL3Routes from 'screens/performanceReview/hooks/useDeerL1PageBasedL3Routes';
import { ReviewDetailedReportGrid } from 'screens/performanceReview/manageAndInsights/ReviewDetailedReport/ReviewDetailedReportGrid';
import { checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';

import { getPersistenceKey } from 'constants/helper';
import { sendPageViewEvent } from 'constants/mixpanelHelper';
import { isNetchexLogin } from 'constants/netChexConstants';

import { PageTabbedNavigationV1 } from 'uiToolkitV1/PageTabbedNavigationV1';

const ViewsReviewsReports = ({ label }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const gridRef = useRef();

  const { moduleName } = useOrganisationSettings();

  const { data: view, isLoading, isError } = useViewFromQueryParam();
  const applicableL2ViewModules = getApplicableL2MyTeamModules('view', moduleName);

  const { l3Options: tabsDeer } = useDeerL1PageBasedL3Routes();

  useEffect(() => {
    if (!isLoading) {
      const cohortToSet = {
        id: view?.id,
        name: view?.name ?? '',
      };
      dispatch(setSelectedView(cohortToSet));
    }

    return () => {
      dispatch(setSelectedView({}));
    };
  }, [dispatch, isLoading, view]);

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        page: 'Custom Team-reviews-reports',
      },
    });
  }, []);

  const gridStatePersistenceKey = useMemo(() => {
    const tabId = checkIntentLandingPagesFeatureEnabled() ? label : 'Explore';
    const uniqueId = 'CustomViewsReviewDetailedReportGrid';

    return getPersistenceKey({
      moduleName,
      viewId: view?.id,
      tabId,
      uniqueId,
    });
  }, [label, moduleName, view?.id]);

  if (isError && !isNetchexLogin) {
    history.replace(staticRoutes.home);
  }

  if (!isLoading && !view?.enabledFeatures.includes('REVIEW') && !isNetchexLogin) {
    history.replace(staticRoutes.home);
  }

  if (checkIntentLandingPagesFeatureEnabled()) {
    return (
      <Layout
        Filters={
          <IntentDropdown
            showKebabMenu
            gridRef={gridRef}
            breadcrumbs={{
              items: [
                {
                  label: view?.name ?? '',
                  to: `${applicableL2ViewModules[0]?.value}?viewId=${view?.id}`,
                },
              ],
              page: {
                label: 'Reviews',
                to: '',
              },
            }}
          />
        }
      >
        <ReviewDetailedReportGrid gridRef={gridRef} gridStatePersistenceKey={gridStatePersistenceKey} />
      </Layout>
    );
  }

  return (
    <>
      <Box pb={1}>
        <PlainTextHeader>{label}</PlainTextHeader>

        <PageTabbedNavigationV1 tabs={tabsDeer} />
      </Box>
      <ReviewDetailedReportGrid gridRef={gridRef} gridStatePersistenceKey={gridStatePersistenceKey} />
    </>
  );
};

export default ViewsReviewsReports;
